import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const MultipleChoiceQuestion = ({ 
  question, 
  options, 
  correctAnswer, 
  onAnswer,
  isAnswered,
  lineContext
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  const handleOptionSelect = (optionId) => {
    if (isAnswered || showFeedback) return;
    setSelectedOption(optionId);
  };

  const handleSubmit = () => {
    if (!selectedOption) return;
    const isCorrect = selectedOption === correctAnswer;
    setShowFeedback(true);
    setTimeout(() => {
      onAnswer(isCorrect);
    }, 2000);
  };

  const getOptionStyle = (optionId) => {
    if (!showFeedback) {
      return selectedOption === optionId
        ? 'border-primary bg-primary/10 shadow-md scale-[1.01]'
        : 'border-border hover:border-primary/50 hover:bg-muted active:scale-[0.99]';
    }

    if (optionId === correctAnswer) {
      return 'border-success bg-success/10';
    }

    if (optionId === selectedOption && optionId !== correctAnswer) {
      return 'border-error bg-error/10';
    }

    return 'border-border opacity-50';
  };

  const getOptionIcon = (optionId) => {
    if (!showFeedback) return null;

    if (optionId === correctAnswer) {
      return <Icon name="CheckCircle" size={24} className="text-success flex-shrink-0" />;
    }

    if (optionId === selectedOption && optionId !== correctAnswer) {
      return <Icon name="XCircle" size={24} className="text-error flex-shrink-0" />;
    }

    return null;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Question card — portrait-optimized */}
      <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
        {lineContext?.gid && (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs text-muted-foreground">
              #{String(lineContext?.gid)?.padStart(4, '0')}
            </span>
            {lineContext?.act && lineContext?.scene && (
              <span className="text-xs text-muted-foreground">
                Act {lineContext?.act}, Scene {lineContext?.scene}
                {lineContext?.lineInScene ? ` · Line ${lineContext?.lineInScene}` : ''}
              </span>
            )}
          </div>
        )}
        <p className="text-base sm:text-lg text-foreground leading-relaxed caption-text">
          {question}
        </p>
      </div>

      {/* Answer options — large touch targets */}
      <div className="space-y-3">
        {options?.map((option, index) => (
          <button
            key={option?.id}
            onClick={() => handleOptionSelect(option?.id)}
            disabled={isAnswered || showFeedback}
            className={`w-full text-left rounded-xl border-2 transition-all duration-200 touch-manipulation select-none ${getOptionStyle(option?.id)} ${
              isAnswered || showFeedback ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
            style={{ minHeight: '64px' }}
          >
            <div className="flex items-center justify-between gap-3 px-4 py-4 sm:py-5">
              <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                {/* Letter badge */}
                <div
                  className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 text-sm font-bold ${
                    selectedOption === option?.id && !showFeedback
                      ? 'border-primary bg-primary text-white' :'border-muted-foreground text-muted-foreground'
                  }`}
                >
                  {showFeedback && option?.id === correctAnswer ? (
                    <Icon name="Check" size={16} className="text-success" />
                  ) : showFeedback && option?.id === selectedOption && option?.id !== correctAnswer ? (
                    <Icon name="X" size={16} className="text-error" />
                  ) : (
                    String.fromCharCode(65 + index)
                  )}
                </div>
                <span className="text-base sm:text-lg text-foreground leading-snug break-words">
                  {option?.text}
                </span>
              </div>
              {getOptionIcon(option?.id)}
            </div>
          </button>
        ))}
      </div>

      {/* Submit button — full-width on mobile for easy tap */}
      {!showFeedback && (
        <div className="pt-2 sm:pt-4">
          <button
            onClick={handleSubmit}
            disabled={!selectedOption || isAnswered}
            className={`w-full sm:w-auto sm:mx-auto sm:flex sm:justify-center py-4 sm:py-3 px-8 rounded-xl font-semibold text-base transition-all duration-200 touch-manipulation ${
              !selectedOption || isAnswered
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-primary text-white hover:opacity-90 active:scale-[0.98] shadow-md'
            }`}
            style={{ minHeight: '56px' }}
          >
            <span className="flex items-center justify-center gap-2">
              Submit Answer
              <Icon name="ArrowRight" size={18} />
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default MultipleChoiceQuestion;